// src/components/modules/CapacitiveSweepChart.jsx
// Visualização do sensor capacitivo FDC2212 — sweep multi-IDRIVE
// 3 charts: Canal 0 (sample+ref), Canal 1 (sample+ref), Δf (ref−sample)
import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
} from 'recharts';
import {
    Paper,
    Typography,
    Box,
    Chip,
    Stack,
    Grid,
    useTheme,
    alpha,
} from '@mui/material';
import { IDRIVE_UA } from '../../utils/sweepFeatures';

function fmtFreq(v) {
    if (v == null) return '—';
    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(3)} MHz`;
    if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(1)} kHz`;
    return `${v.toFixed(0)} Hz`;
}

function fmtAxisTick(v) {
    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (Math.abs(v) >= 1e3) return `${(v / 1e3).toFixed(0)}k`;
    return v.toFixed(0);
}

function SweepPanel({ data, title, yLabel, lines, theme }) {
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data} margin={{ top: 5, right: 10, left: 8, bottom: 22 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis
                        dataKey="idrive"
                        tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                        axisLine={false} tickLine={false}
                        label={{ value: 'IDRIVE (µA)', position: 'insideBottom', dy: 18, fontSize: 9, fill: theme.palette.text.secondary }}
                    />
                    <YAxis
                        tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                        axisLine={false} tickLine={false} width={60}
                        tickFormatter={fmtAxisTick}
                        domain={['auto', 'auto']}
                        label={{ value: yLabel, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' }, fontSize: 9, fill: theme.palette.text.secondary }}
                    />
                    <Tooltip
                        contentStyle={{ fontSize: 12, background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                        formatter={(val) => [fmtFreq(val)]}
                        labelFormatter={(v) => `IDRIVE: ${v} µA`}
                    />
                    <Legend verticalAlign="top" height={24} iconType="plainline" wrapperStyle={{ fontSize: 11 }} />
                    {lines.map((l) => (
                        <Line key={l.dataKey} dataKey={l.dataKey} name={l.name} stroke={l.color}
                            strokeWidth={l.dashed ? 1.5 : 2} strokeDasharray={l.dashed ? '5 3' : undefined}
                            dot={false} type="monotone" connectNulls />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </Paper>
    );
}

/**
 * @param {{ capacitiveSensor: object }} props
 */
export default function CapacitiveSweepChart({ capacitiveSensor }) {
    const theme = useTheme();
    const cs = capacitiveSensor;
    const hasSweep = cs?.sweep?.length > 0;
    const hasRef = cs?.reference?.length > 0;

    const { ch0Data, ch1Data, deltaData } = useMemo(() => {
        if (!hasSweep) return { ch0Data: [], ch1Data: [], deltaData: [] };
        const sweep = cs.sweep;
        const ref = cs.reference;
        const ch0 = [], ch1 = [], delta = [];
        for (let i = 0; i < sweep.length; i++) {
            const idrive = IDRIVE_UA[i] ?? i;
            const s0 = sweep[i]?.[0], s1 = sweep[i]?.[1];
            const r0 = ref?.[i]?.[0], r1 = ref?.[i]?.[1];
            ch0.push({ idrive, sample: s0, ref: r0 ?? null });
            ch1.push({ idrive, sample: s1, ref: r1 ?? null });
            delta.push({
                idrive,
                ch0: r0 != null && s0 != null ? r0 - s0 : null,
                ch1: r1 != null && s1 != null ? r1 - s1 : null,
            });
        }
        return { ch0Data: ch0, ch1Data: ch1, deltaData: delta };
    }, [cs, hasSweep, hasRef]);

    const ch0Color = theme.palette.text.primary;
    const ch1Color = theme.palette.grey[500];
    const refColor = theme.palette.grey[400];

    if (!cs) return null;

    const ch0 = cs.channel0;
    const ch1 = cs.channel1;

    return (
        <>
            {/* 3 Sweep Charts */}
            {hasSweep && ch0Data.length > 0 ? (
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.divider, 0.04), borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <SweepPanel
                                data={ch0Data} title="Canal 0" yLabel="Freq (Hz)" theme={theme}
                                lines={[
                                    { dataKey: 'sample', name: 'Amostra', color: ch0Color },
                                    ...(hasRef ? [{ dataKey: 'ref', name: 'Referência', color: refColor, dashed: true }] : []),
                                ]}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.divider, 0.04), borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <SweepPanel
                                data={ch1Data} title="Canal 1" yLabel="Freq (Hz)" theme={theme}
                                lines={[
                                    { dataKey: 'sample', name: 'Amostra', color: ch1Color },
                                    ...(hasRef ? [{ dataKey: 'ref', name: 'Referência', color: refColor, dashed: true }] : []),
                                ]}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.divider, 0.04), borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <SweepPanel
                                data={deltaData} title="Δf (Ref − Amostra)" yLabel="Δf (Hz)" theme={theme}
                                lines={[
                                    { dataKey: 'ch0', name: 'Canal 0', color: ch0Color },
                                    { dataKey: 'ch1', name: 'Canal 1', color: ch1Color },
                                ]}
                            />
                        </Box>
                    </Grid>
                </Grid>
            ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.disabled">
                        Sem dados de sweep
                    </Typography>
                </Box>
            )}

            {/* Frequência instantânea + status de erro — só faz sentido junto
                do gráfico de sweep; sem ele, esse valor já aparece no card
                "Frequência Ressonância" no topo da página, mostrar de novo
                aqui seria redundante. */}
            {hasSweep && ch0Data.length > 0 && (
                <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
                    <Paper
                        variant="outlined"
                        sx={{ px: 1.5, py: 0.8, display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 140 }}
                    >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ch0Color, flexShrink: 0 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary" lineHeight={1}>Canal 0</Typography>
                            <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                                {ch0 ? fmtFreq(ch0.frequency) : '—'}
                            </Typography>
                        </Box>
                        {ch0?.errAw && (
                            <Chip label="AmpWarn" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                        )}
                        {ch0?.errWd && (
                            <Chip label="Watchdog" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                        )}
                    </Paper>
                    <Paper
                        variant="outlined"
                        sx={{ px: 1.5, py: 0.8, display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 140 }}
                    >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: ch1Color, flexShrink: 0 }} />
                        <Box>
                            <Typography variant="caption" color="text.secondary" lineHeight={1}>Canal 1</Typography>
                            <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
                                {ch1 ? fmtFreq(ch1.frequency) : '—'}
                            </Typography>
                        </Box>
                        {ch1?.errAw && (
                            <Chip label="AmpWarn" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                        )}
                        {ch1?.errWd && (
                            <Chip label="Watchdog" size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 9 }} />
                        )}
                    </Paper>
                </Stack>
            )}
        </>
    );
}
